import { useRef } from 'react';
import './Toast.css';

/**
 * Toast — single notification item.
 * Not used directly — use useToast() hook from ToastContext.
 *
 * UX basis: NN/g (confirmations, optional dismiss, 200–400ms enter),
 *           Baymard (redundant cues: icon + left border + color),
 *           Luxury design (restraint, 8px grid, micro copy).
 *
 * Props:
 *   id        string   — unique id from context
 *   message   string   — notification text
 *   type      string   — 'success' | 'error' | 'info' | 'warning'
 *   duration  number   — ms before auto-dismiss
 *   visible   boolean  — controls enter/exit animation
 *   onClose   fn       — called to dismiss
 */

const ICONS = {
  success: (
    <>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </>
  ),
  error: (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
      <line x1="12" y1="8" x2="12" y2="11" />
    </>
  ),
  warning: (
    <>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </>
  ),
};

function Toast({ id, message, type = 'success', duration = 3000, visible, onClose }) {
  /*
    Progress bar: always in DOM, animation restarted via key on visibility change.
    Avoids the remount flash from conditional rendering.
  */
  const progressKey = useRef(0);
  if (visible) progressKey.current += 1;

  return (
    <div
      className={`toast toast--${type}${visible ? ' toast--visible' : ''}`}
      role={type === 'error' ? 'alert' : 'status'}
      aria-live={type === 'error' ? 'assertive' : 'polite'}
      aria-atomic="true"
      data-toast-id={id}
    >
      <div className="toast__content">
        <svg
          className="toast__icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          {ICONS[type] ?? ICONS.info}
        </svg>
        <span className="toast__message">{message}</span>
      </div>

      <button
        type="button"
        className="toast__close"
        onClick={onClose}
        aria-label="Dismiss notification"
        tabIndex={visible ? 0 : -1}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {/*
        Progress bar: key changes on each show → animation restarts cleanly.
        Always in DOM (no conditional render) → no remount flash.
        Hidden via opacity when not visible.
      */}
      <div
        key={progressKey.current}
        className={`toast__progress${visible ? ' toast__progress--active' : ''}`}
        style={{ animationDuration: `${duration}ms` }}
        aria-hidden="true"
      />
    </div>
  );
}

export default Toast;
